// ============================================================
// Lenses API — CRUD for custom data dimension mappings
// ============================================================

import type {
    APIResponse,
    LensDefinition,
    LensTemplate,
    LensTemplateId,
} from '@yourownmetric/shared'

import { apiSlice } from './apiSlice'

// --- Param types ---

interface ListLensesParams {
    projectId: string
}

interface CreateLensParams {
    projectId: string
    name: string
    templateId?: LensTemplateId
    dimensions?: Array<{ name: string; propertyKey: string; description?: string }>
    metrics?: Array<{ name: string; propertyKey: string; description?: string }>
}

interface UpdateLensParams {
    id: string
    name?: string
    dimensions?: Array<{ name: string; propertyKey: string; description?: string }>
    metrics?: Array<{ name: string; propertyKey: string; description?: string }>
    active?: boolean
}

interface TemplateSummary {
    id: LensTemplateId
    name: string
    description: string
    icon: string
    dimensionCount: number
    metricCount: number
}

export const lensesApi = apiSlice.injectEndpoints({
    endpoints: builder => ({
        listLenses: builder.query<LensDefinition[], ListLensesParams>({
            query: ({ projectId }) => `/lenses?projectId=${projectId}`,
            transformResponse: (res: APIResponse<LensDefinition[]>) => res.data!,
            providesTags: ['Lenses'],
        }),

        getLens: builder.query<LensDefinition, string>({
            query: id => `/lenses/${id}`,
            transformResponse: (res: APIResponse<LensDefinition>) => res.data!,
            providesTags: ['Lenses'],
        }),

        createLens: builder.mutation<LensDefinition, CreateLensParams>({
            query: body => ({ url: '/lenses', method: 'POST', body }),
            transformResponse: (res: APIResponse<LensDefinition>) => res.data!,
            invalidatesTags: ['Lenses'],
        }),

        updateLens: builder.mutation<LensDefinition, UpdateLensParams>({
            query: ({ id, ...body }) => ({ url: `/lenses/${id}`, method: 'PUT', body }),
            transformResponse: (res: APIResponse<LensDefinition>) => res.data!,
            invalidatesTags: ['Lenses'],
        }),

        deleteLens: builder.mutation<{ deleted: string }, string>({
            query: id => ({ url: `/lenses/${id}`, method: 'DELETE' }),
            transformResponse: (res: APIResponse<{ deleted: string }>) => res.data!,
            invalidatesTags: ['Lenses'],
        }),

        listLensTemplates: builder.query<TemplateSummary[], void>({
            query: () => '/lenses/templates/list',
            transformResponse: (res: APIResponse<TemplateSummary[]>) => res.data!,
        }),

        getLensTemplate: builder.query<LensTemplate, LensTemplateId>({
            query: id => `/lenses/templates/${id}`,
            transformResponse: (res: APIResponse<LensTemplate>) => res.data!,
        }),
    }),
})

export const {
    useListLensesQuery,
    useGetLensQuery,
    useCreateLensMutation,
    useUpdateLensMutation,
    useDeleteLensMutation,
    useListLensTemplatesQuery,
    useGetLensTemplateQuery,
} = lensesApi
